import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Euro, TrendingUp, DollarSign, Percent, ImageIcon, Bell } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { translateStatus } from '../i18n/translations';
import { StatusFlowBar } from '../components/StatusFlowBar';

const STATUS_OPTIONS = [
  { value: 'commandée', label: 'Commandée' },
  { value: 'impression', label: 'Impression' },
  { value: 'couture', label: 'Couture' },
  { value: 'livraison bureau', label: 'Livraison bureau' },
  { value: 'en cours de livraison client', label: 'En cours livraison' },
  { value: 'livrée', label: 'Livrée' }
];

export const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [monthlySales, setMonthlySales] = useState([]);
  const [commercialMonthly, setCommercialMonthly] = useState([]);
  const [currency, setCurrency] = useState('EUR');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState(25000);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [commercialKpi, setCommercialKpi] = useState(null);
  const [commercialArchives, setCommercialArchives] = useState([]);
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [alertNotifs, setAlertNotifs] = useState([]);
  const prevAlertKeys = React.useRef(null);

  useEffect(() => {
    fetchData();
    // Polling for orange/red notifications
    const interval = setInterval(() => fetchData(true), 300000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const fetchData = async (isPolling = false) => {
    setLoading(true);
    try {
      const settingsRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings`,
        { withCredentials: true }
      );
      setRate(settingsRes.data.eur_vnd_rate);

      const companyParam = selectedCompany !== 'all' ? `?company=${encodeURIComponent(selectedCompany)}` : '';
      const promises = [
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/stats${companyParam}`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/alerts`, { withCredentials: true })
      ];
      if (user?.role === 'admin') {
        promises.push(axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/monthly-sales`, { withCredentials: true }));
      }
      if (user?.role === 'commercial') {
        promises.push(axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/commercial-monthly`, { withCredentials: true }));
        promises.push(axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/commercial-kpi`, { withCredentials: true }));
        promises.push(axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/commercial/archives`, { withCredentials: true }));
      }

      const results = await Promise.all(promises);
      setStats(results[0].data);
      setAlerts(results[1].data);

      // Detect new orange/red alerts for notification
      if (isPolling) {
        const currentKeys = new Set((results[1].data || []).filter(a => a.color === 'orange' || a.color === 'red').map(a => `${a.order_id}-${a.color}`));
        const prevKeys = prevAlertKeys.current || new Set();
        const newAlerts = [...currentKeys].filter(k => !prevKeys.has(k));
        if (newAlerts.length > 0) {
          const orangeCount = newAlerts.filter(k => k.endsWith('-orange')).length;
          const redCount = newAlerts.filter(k => k.endsWith('-red')).length;
          const msgs = [];
          if (redCount > 0) msgs.push(t('alert_red_notification'));
          if (orangeCount > 0) msgs.push(t('alert_orange_notification'));
          setAlertNotifs(prev => [...prev, ...msgs.map(msg => ({ id: Date.now() + Math.random(), msg }))]);
          setTimeout(() => setAlertNotifs([]), 8000);
        }
        prevAlertKeys.current = currentKeys;
      } else {
        prevAlertKeys.current = new Set((results[1].data || []).filter(a => a.color === 'orange' || a.color === 'red').map(a => `${a.order_id}-${a.color}`));
      }
      if (user?.role === 'admin' && results[2]) setMonthlySales(results[2].data);
      if (user?.role === 'commercial') {
        if (results[2]) setCommercialMonthly(results[2].data);
        if (results[3]) setCommercialKpi(results[3].data);
        if (results[4]) setCommercialArchives(results[4].data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (currency === 'EUR') {
      return `${value.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} \u20ac`;
    }
    return `${(value * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} \u20ab`;
  };

  const getAlertBadgeColor = (color) => {
    switch (color) {
      case 'green': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'orange': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'red': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getRowBgColor = (color) => {
    switch (color) {
      case 'green': return 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100';
      case 'orange': return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
      case 'red': return 'bg-red-50 border-red-200 hover:bg-red-100';
      default: return 'bg-slate-50 border-slate-200 hover:bg-slate-100';
    }
  };

  const handleAlertClick = async (alert) => {
    if (user?.role !== 'admin') return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${alert.order_id}`,
        { withCredentials: true }
      );
      setOrderDetail(res.data);
      setOrderDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement de la commande');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!orderDetail) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderDetail.id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Statut mis à jour: ${newStatus}`);
      setOrderDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-sm text-slate-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isCommercial = user?.role === 'commercial';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Orange/Red alert notifications */}
        {alertNotifs.length > 0 && (
          <div data-testid="color-alert-notification" className="space-y-2">
            {alertNotifs.map((n) => (
              <div key={n.id} className={`rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg ${
                n.msg.includes(t('alert_red_notification')) ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
              }`}>
                <Bell size={20} className="animate-bounce flex-shrink-0" />
                <span className="font-semibold">{n.msg}</span>
                <button onClick={() => setAlertNotifs(prev => prev.filter(x => x.id !== n.id))} className="ml-auto opacity-70 hover:opacity-100 text-lg font-bold">&times;</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('dashboard_title')}</h1>
            <p className="text-sm text-slate-600 mt-1">{t('dashboard_subtitle')}</p>
            <StatusFlowBar />
          </div>
          <div className="flex gap-3">
            {!isCommercial && (
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger data-testid="company-filter" className="w-48 border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all_companies')}</SelectItem>
                  <SelectItem value="Panther VN">Panther VN</SelectItem>
                  <SelectItem value="Panther France">Panther France</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!isCommercial && (
              <Tabs value={currency} onValueChange={setCurrency} className="w-auto">
                <TabsList className="bg-slate-100">
                  <TabsTrigger data-testid="currency-eur" value="EUR" className="data-[state=active]:bg-white">EUR</TabsTrigger>
                  <TabsTrigger data-testid="currency-vnd" value="VND" className="data-[state=active]:bg-white">VND</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          </div>
        </div>

        {/* Admin charts */}
        {user?.role === 'admin' && monthlySales.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card data-testid="chart-monthly-sales" className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">
                  {t('monthly_sales')} {new Date().getFullYear()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlySales}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                      formatter={(value) => [`${value.toLocaleString('fr-FR')} \u20ac`, '']}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="total" name="CA Total" fill="#0f172a" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="costs" name="Co&ucirc;ts" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="margin" name="Marge" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="chart-company-sales" className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">
                  {t('france_vs_vn')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlySales}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                      formatter={(value) => [`${value.toLocaleString('fr-FR')} \u20ac`, '']}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="panther_france" name="Panther France" fill="#1e40af" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="panther_vn" name="Panther VN" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Commercial chart */}
        {isCommercial && commercialMonthly.length > 0 && (
          <Card data-testid="chart-commercial-monthly" className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">
                {t('my_ca_commissions')} {new Date().getFullYear()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={commercialMonthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                    formatter={(value) => [`${value.toLocaleString('fr-FR')} \u20ac`, '']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="ca" name="Chiffre d'affaires" fill="#0f172a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="commission" name="Commissions" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {isCommercial ? (
            <>
              <Card data-testid="stat-month-ca" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('month_ca')}</CardTitle>
                  <Euro className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{(commercialKpi?.month_ca || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} &euro;</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-year-ca" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('year_ca')}</CardTitle>
                  <TrendingUp className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{(commercialKpi?.year_ca || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} &euro;</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-commission" className="border-slate-200 shadow-sm bg-slate-900 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-300 uppercase tracking-wider">{t('month_commission')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{(commercialKpi?.month_commission || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} &euro;</div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card data-testid="stat-sales" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('total_sales')}</CardTitle>
                  <Euro className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.total_sales_eur || 0)}</div>
                  <p className="text-xs text-slate-500 mt-1">{stats?.total_orders || 0} {t('orders_count')}</p>
                </CardContent>
              </Card>
              <Card data-testid="stat-costs" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('total_costs')}</CardTitle>
                  <DollarSign className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.total_costs || 0)}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-margin" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('gross_margin')}</CardTitle>
                  <TrendingUp className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.total_margin || 0)}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-commissions" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('commissions')}</CardTitle>
                  <Percent className="h-4 w-4 text-slate-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.total_commissions || 0)}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-expenses" className="border-slate-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-600 uppercase tracking-wider">{t('expenses')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold text-slate-900">{formatCurrency(stats?.total_expenses || 0)}</div>
                </CardContent>
              </Card>
              <Card data-testid="stat-net-profit" className="border-slate-200 shadow-sm bg-slate-900 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-300 uppercase tracking-wider">{t('net_profit')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-semibold">{formatCurrency(stats?.net_profit || 0)}</div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <Card data-testid="delivery-alerts" className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">{t('orders_in_progress')}</CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="text-sm text-slate-500">{t('no_orders_in_progress')}</p>
            ) : (
              <div className="space-y-2">
                {(isCommercial
                  ? [...alerts].sort((a, b) => (a.client_delivery_date || '9999').localeCompare(b.client_delivery_date || '9999') || a.order_number.localeCompare(b.order_number))
                  : alerts
                ).map((alert) => (
                  <div
                    key={alert.order_id}
                    data-testid={`alert-${alert.color}`}
                    onClick={() => handleAlertClick(alert)}
                    className={`flex items-center justify-between p-3 rounded-md border cursor-pointer transition-colors ${getRowBgColor(alert.color)}`}
                  >
                    {/* Image thumbnail */}
                    <div className="mr-3 flex-shrink-0">
                      {alert.images && alert.images.length > 0 ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${alert.images[0]}`}
                          alt=""
                          data-testid="order-thumbnail"
                          className="w-12 h-12 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={(e) => { e.stopPropagation(); setImagePopupImages(alert.images); setImagePopupOpen(true); }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-200 rounded border border-slate-300 flex items-center justify-center">
                          <ImageIcon size={16} className="text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">
                        {alert.order_number} - {alert.client_name}
                      </p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <Badge className={`text-xs border uppercase ${
                          alert.status === 'commandée' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                          alert.status === 'impression' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                          alert.status === 'couture' ? 'bg-indigo-100 text-indigo-800 border-indigo-200' :
                          alert.status === 'livraison bureau' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                          alert.status === 'en cours de livraison client' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                          'bg-slate-100 text-slate-800 border-slate-200'
                        }`}>
                          {translateStatus(alert.status, t)}
                        </Badge>
                        {!isCommercial && <span className="text-xs text-slate-600">{alert.company}</span>}
                        {!isCommercial && alert.client_delivery_date && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            alert.color === 'green' ? 'bg-emerald-100 text-emerald-800' :
                            alert.color === 'orange' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {t('delivery_client')}: {new Date(alert.client_delivery_date).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {isCommercial && (
                          <span className="text-sm font-bold text-emerald-700">
                            Commission: {(alert.commercial_commission || 0).toFixed(2)} &euro;
                          </span>
                        )}
                        {isCommercial && alert.tracking_number && (
                          <span className="text-xs text-slate-500">N° {alert.tracking_number}</span>
                        )}
                        {isCommercial && alert.tracking_link && (
                          <a href={alert.tracking_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                            <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs cursor-pointer hover:bg-blue-200">TRACK LINK</Badge>
                          </a>
                        )}
                      </div>
                    </div>
                    {!isCommercial && alert.bureau_delivery_date && (
                      <div className={`text-right ml-4 px-3 py-1 rounded ${
                        alert.color === 'green' ? 'bg-emerald-200 text-emerald-900' :
                        alert.color === 'orange' ? 'bg-amber-200 text-amber-900' :
                        'bg-red-200 text-red-900'
                      }`}>
                        <p className="text-xs font-medium uppercase">{t('delivery_office')}</p>
                        <p className="text-sm font-bold">{new Date(alert.bureau_delivery_date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    )}
                    {isCommercial && alert.client_delivery_date && (
                      <div className={`text-right ml-4 px-3 py-1 rounded ${
                        alert.color === 'green' ? 'bg-emerald-200 text-emerald-900' :
                        alert.color === 'orange' ? 'bg-amber-200 text-amber-900' :
                        'bg-red-200 text-red-900'
                      }`}>
                        <p className="text-xs font-medium uppercase">{t('delivery_client')}</p>
                        <p className="text-sm font-bold">{new Date(alert.client_delivery_date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commercial Archives */}
        {isCommercial && commercialArchives.length > 0 && (
          <Card data-testid="commercial-archives" className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-medium">{t('archives')}</CardTitle>
              <p className="text-xs text-slate-500">{t('delivered_orders_by_month')}</p>
            </CardHeader>
            <CardContent>
              {(() => {
                const grouped = {};
                commercialArchives.forEach(a => {
                  if (!grouped[a.month_label]) grouped[a.month_label] = [];
                  grouped[a.month_label].push(a);
                });
                return Object.entries(grouped).map(([month, archOrders]) => (
                  <div key={month} className="mb-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2 uppercase">{month}</h3>
                    <div className="space-y-1">
                      {archOrders.map(a => (
                        <div key={a.id} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-100">
                          <div>
                            <span className="text-sm font-medium">{a.order_number}</span>
                            <span className="text-sm text-slate-600 ml-2">- {a.client_name} {a.client_firstname}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">{a.sale_price_eur?.toFixed(2)} &euro;</span>
                            <span className="text-sm font-bold text-emerald-700">{a.commercial_commission?.toFixed(2)} &euro;</span>
                            {a.tracking_link && (
                              <a href={a.tracking_link} target="_blank" rel="noreferrer">
                                <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs cursor-pointer hover:bg-blue-200">TRACK</Badge>
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}
            </CardContent>
          </Card>
        )}

        <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Commande {orderDetail?.order_number}</DialogTitle>
            </DialogHeader>
            {orderDetail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-slate-500">Client</Label>
                    <p className="font-medium">{orderDetail.client_name} {orderDetail.client_firstname}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Pays</Label>
                    <p className="font-medium">{orderDetail.client_country}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">T&eacute;l&eacute;phone</Label>
                    <p>{orderDetail.client_phone}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <p>{orderDetail.client_email}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Adresse</Label>
                    <p>{orderDetail.client_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Soci&eacute;t&eacute;</Label>
                    <p className="font-medium">{orderDetail.company}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Pi&egrave;ces</Label>
                    <p>{orderDetail.num_pieces} {orderDetail.piece_type}</p>
                    {orderDetail.order_lines && orderDetail.order_lines.length > 0 && (
                      <p className="text-xs text-slate-400 mt-0.5">{orderDetail.order_lines.length} {t('product_lines')}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Urgence</Label>
                    <p>{orderDetail.urgency}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Date commande</Label>
                    <p>{new Date(orderDetail.order_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Livraison bureau</Label>
                    <p className="font-semibold">{orderDetail.bureau_delivery_date ? new Date(orderDetail.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Livraison client</Label>
                    <p className="font-semibold">{orderDetail.client_delivery_date ? new Date(orderDetail.client_delivery_date).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('commercial')}</Label>
                    <p>{orderDetail.commercial_name}</p>
                  </div>
                  {orderDetail.tracking_number && (
                    <div>
                      <Label className="text-xs text-slate-500">{t('tracking_number')}</Label>
                      <p>{orderDetail.tracking_number}</p>
                    </div>
                  )}
                  {orderDetail.tracking_link && (
                    <div>
                      <Label className="text-xs text-slate-500">{t('tracking_link')}</Label>
                      <a href={orderDetail.tracking_link} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">{orderDetail.tracking_link}</a>
                    </div>
                  )}
                </div>

                {/* Détail des pièces (lignes de commande) */}
                {orderDetail.order_lines && orderDetail.order_lines.length > 0 && (
                  <div data-testid="order-lines-detail" className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">{t('product')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('quantity')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('unit_cost')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('unit_price')}</th>
                          <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">{t('weight')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetail.order_lines.map((line, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium text-slate-800">{line.product_name}</td>
                            <td className="px-3 py-2 text-right text-slate-700">{line.quantity}</td>
                            <td className="px-3 py-2 text-right text-slate-700">
                              {(line.unit_cost || 0).toFixed(2)} &euro;
                              <div className="text-[10px] text-slate-400">{((line.unit_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700">
                              {(line.unit_price || 0).toFixed(2)} &euro;
                              <div className="text-[10px] text-slate-400">{((line.unit_price || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                            </td>
                            <td className="px-3 py-2 text-right text-slate-700">{(line.weight_kg || 0).toFixed(2)} kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* PROFIT ESTIMÉ - Green Box */}
                <div data-testid="estimated-profit-box" className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 mt-2">
                  <h3 className="text-sm font-bold text-emerald-800 mb-3">{t('estimated_profit')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-end">
                      <span className="text-emerald-700">{t('selling_price')}</span>
                      <div className="text-right">
                        <span className="font-bold text-emerald-900">{(orderDetail.sale_price_eur || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">{((orderDetail.sale_price_eur || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('product_cost_label')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(orderDetail.material_cost || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((orderDetail.material_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('transport_cost')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(orderDetail.transport_cost || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((orderDetail.transport_cost || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-end text-red-700">
                      <span>- {t('commission')}</span>
                      <div className="text-right">
                        <span className="font-medium">-{(orderDetail.commercial_commission || 0).toFixed(2)} &euro;</span>
                        <div className="text-[10px] text-slate-400">-{((orderDetail.commercial_commission || 0) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;</div>
                      </div>
                    </div>
                    <div className="border-t border-emerald-300 pt-2 flex justify-between items-end">
                      <span className="font-bold text-emerald-900">= {t('estimated_profit')}</span>
                      <div className="text-right">
                        <span className="font-bold text-lg text-emerald-900">
                          {((orderDetail.sale_price_eur || 0) - (orderDetail.material_cost || 0) - (orderDetail.transport_cost || 0) - (orderDetail.commercial_commission || 0)).toFixed(2)} &euro;
                        </span>
                        <div className="text-[10px] text-slate-400">
                          {(((orderDetail.sale_price_eur || 0) - (orderDetail.material_cost || 0) - (orderDetail.transport_cost || 0) - (orderDetail.commercial_commission || 0)) * rate).toLocaleString('vi-VN', { maximumFractionDigits: 0 })} &#8363;
                        </div>
                      </div>
                    </div>
                    {(() => {
                      const profit = (orderDetail.sale_price_eur || 0) - (orderDetail.material_cost || 0) - (orderDetail.transport_cost || 0) - (orderDetail.commercial_commission || 0);
                      const marginPct = (orderDetail.sale_price_eur || 0) !== 0 ? (profit / (orderDetail.sale_price_eur || 1)) * 100 : 0;
                      return (
                        <div data-testid="margin-percent" className="flex justify-between items-center pt-1">
                          <span className="font-semibold text-emerald-800 text-xs uppercase">{t('margin')} %</span>
                          <span className={`font-bold text-sm ${marginPct >= 0 ? 'text-emerald-900' : 'text-red-600'}`}>{marginPct.toFixed(1)} %</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {user?.role === 'admin' && (
                  <div className="border-t border-slate-200 pt-4">
                    <Label className="text-sm font-medium">{t('change_status')}</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {STATUS_OPTIONS.map(s => (
                        <Button
                          key={s.value}
                          size="sm"
                          data-testid={`status-btn-${s.value}`}
                          variant={orderDetail.status === s.value ? "default" : "outline"}
                          className={`uppercase ${orderDetail.status === s.value ? "bg-slate-900" : ""}`}
                          onClick={() => handleStatusChange(s.value)}
                          disabled={orderDetail.status === s.value}
                        >
                          {translateStatus(s.value, t)}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
          <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
            <DialogHeader>
              <DialogTitle>{t('image_popup_title')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {imagePopupImages.map((img, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full rounded-lg border border-slate-200" />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
